/**
 * Shared normalisation for 8x8 catalogue / licence items.
 *
 * The backend returns items in its own shape; the catalog page, the service
 * profile licence editor, and the service licence tree all consume the same
 * camelCase shape. Keep this mapping in one place so every screen nests and
 * renders relationships identically.
 *
 * API → internal:
 *   parentID      → parentId      (drives tree nesting; '' for roots)
 *   reference1    → offeringId
 *   visible       → visibility    ("visible" | "hidden")
 *   optionalChild → rule          ("optional" | "required" mandatory child | "" root)
 *
 * @param {object} item       Raw catalogue item from the API.
 * @param {object[]} allItems Full raw list, used to resolve parentName.
 * @returns {object} Normalised item with both raw and internal field names.
 */
export const normaliseCatalogueItem = (item, allItems = []) => {
    const parent = allItems.find((i) => i.id === item.parentID);
    return {
        ...item,
        parentId: item.parentID ?? '',
        parentName: parent?.name ?? null,
        offeringId: item.reference1 ?? '',
        visibility: item.visible ? 'visible' : 'hidden',
        rule: item.optionalChild ? 'optional' : item.parentID ? 'required' : '',
    };
};

/**
 * Determines whether a catalogue item is a mandatory ("required") child.
 * Prefers the `rule` string (tolerating the legacy "must"/"can" values); when
 * `rule` hasn't been normalised (or is missing), derives it from the raw
 * `optionalChild` and `parentId`/`parentID` fields instead.
 * @param {object} item
 * @returns {boolean}
 */
export const isRequiredRule = (item = {}) => {
    if (item.rule === 'required' || item.rule === 'must') return true;
    if (item.rule === 'optional' || item.rule === 'can') return false;
    const parentId = item.parentId || item.parentID;
    return Boolean(parentId) && !item.optionalChild;
};

/**
 * Normalise a full catalogue list, resolving parentName against the same list.
 *
 * @param {object[]} items Raw catalogue items from the API.
 * @returns {object[]} Normalised items.
 */
export const normaliseCatalogue = (items = []) =>
    (Array.isArray(items) ? items : []).map((item) =>
        normaliseCatalogueItem(item, items),
    );

/**
 * Reduce a catalogue item (raw or normalised) to the raw fields the
 * PUT /Service/EightByEight schema accepts, dropping the internal-only fields
 * added by normaliseCatalogueItem (parentId, parentName, offeringId, etc.).
 * @param {object} item
 * @returns {object} catalogItem matching the API schema.
 */
export const toCatalogItemPayload = (item = {}) => ({
    id: item.id,
    platformID: item.platformID,
    parentID: item.parentID ?? null,
    name: item.name,
    description: item.description,
    sku: item.sku,
    reference1: item.reference1,
    reference2: item.reference2,
    reference3: item.reference3,
    visible: item.visible,
    optionalChild: item.optionalChild,
});
